import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Grid, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { style } from "./style";

import Dialog from "../Dialog";
import Modal from "../Modal";
import GetImages from "../GetImages";
import Button from "../Button";

import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { useGeolocation } from "@/utils/hooks/useGeolocation";
import { useGooglePlaces } from "@/utils/hooks/useGooglePlaces";
import { storage } from "@/utils/Storage";

import { IButtonVariant } from "@/models/button";
import { ISessionKeys } from "@/models";

import { useAppDispatch } from "@/store/hooks";
import { setLocation } from "@/store/slices/config";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangeLocation: React.FC<IProps> = ({ isOpen, onClose }) => {
  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { getLocation } = useGeolocation();

  const { searchResults, isLoading, searchPlaces, getPlaceDetails } =
    useGooglePlaces({
      apiKey: import.meta.env.VITE_APP_GOOGLE_PLACES_API_KEY,
    });

  const [searchInput, setSearchInput] = useState("");

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    []
  );

  const handlePlaceSelect = useCallback(
    async (placeId: string, placeName: string) => {
      try {
        const place = await getPlaceDetails(placeId);
        if (place.geometry?.location) {
          const latitude = place.geometry.location.lat();
          const longitude = place.geometry.location.lng();
          storage.setSessionToken(
            ISessionKeys.LOCATION,
            JSON.stringify({ latitude, longitude })
          );
          dispatch(setLocation(placeName));
          onClose();
          navigate("/find-doctor", { state: { navItem: 1 } });
        }
      } catch (error) {
        console.error("Error getting place details:", error);
      }
    },
    [getPlaceDetails, dispatch, onClose, navigate]
  );

  const handleCurrentLocation = useCallback(() => {
    getLocation(onClose);
  }, [getLocation, onClose]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchInput) {
        searchPlaces(searchInput);
      }
    }, 1000);

    return () => clearTimeout(debounceTimer);
  }, [searchInput, searchPlaces]);

  const searchResultsList = useMemo(
    () => (
      <Stack direction={"column"} gap={0.8} mb={2.7}>
        {searchResults.map((item) => (
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={0.8}
            key={item.place_id}
            onClick={() =>
              handlePlaceSelect(
                item.place_id,
                `${item.structured_formatting.main_text}, ${item.structured_formatting.secondary_text}`
              )
            }
            sx={{ cursor: "pointer" }}
          >
            <GetImages name="SearchLocationIcon" width="16" height="16" />
            <Stack direction={"column"}>
              <Stack direction={"row"} alignItems={"center"} gap={0.8}>
                <Typography component={"p"} sx={style.cityName}>
                  {item.structured_formatting.main_text}
                </Typography>
              </Stack>
              <Typography component={"p"} sx={style.stateName}>
                {item.structured_formatting.secondary_text}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
    ),
    [searchResults, handlePlaceSelect]
  );

  const ChangeLocationModal = memo(() => {
    return (
      <>
        <Stack gap={1} px={1.6} py={isMobile || isTablet ? 0 : 1.6}>
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <GetImages name="LocationAccessSmallIcon" width="20" height="20" />
            <Typography component={"h5"} sx={style.container}>
              Change Location
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={1}
            sx={style.searchInputContainer}
          >
            <input
              placeholder={`Search for city, Area, Pincode`}
              autoFocus
              value={searchInput}
              onChange={handleSearchInputChange}
            />
            <Grid item sx={style.searchIcon}>
              <GetImages name="NavSearchIcon" width="24" height="24" />
            </Grid>
          </Stack>
          <Stack direction={"column"} gap={0.8}>
            <Typography component={"p"} sx={style.searchResults}>
              {isLoading
                ? "Searching..."
                : `${searchResults.length} Search Results`}
            </Typography>
            {searchResultsList}
          </Stack>
        </Stack>
        <Stack
          direction={"row"}
          gap={1}
          alignItems={"center"}
          sx={style.buttonContainer}
        >
          <Button
            variant={IButtonVariant.WHITE}
            btnTrigger={handleCurrentLocation}
          >
            <GetImages name={"UseCurrentLocationIcon"} width="26" height="20" />
            <Typography component={"p"} sx={style.buttonText}>
              Use Current Location
            </Typography>
          </Button>
        </Stack>
      </>
    );
  });

  return (
    <>
      {isMobile || isTablet ? (
        <Modal
          isOpen={isOpen}
          closeHandler={onClose}
          lineBottomBackground="white"
        >
          <ChangeLocationModal />
        </Modal>
      ) : (
        <Dialog
          isOpen={isOpen}
          closeHandler={onClose}
          paperProps={{
            sx: {
              minWidth: "46.5rem",
              position: "absolute",
              top: 85, // Adjust distance from top
              left: 140, // Adjust distance from right
              margin: 0,
            },
          }}
        >
          <ChangeLocationModal />
        </Dialog>
      )}
    </>
  );
};

export default memo(ChangeLocation);
